import React, { useEffect, useState } from 'react';
import { Chip } from '@mui/material';
import { Ticket } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getMyCoupons, type MyCoupon } from '../../services/payment.api';
import type { PurchaseTarget } from '../../hooks/usePurchaseCheckout';

interface CouponPickerProps {
  target: PurchaseTarget | null;
  appliedCodes: string[];
  disabled?: boolean;
  onPick: (code: string) => void;
}

function daysLeft(expiresAt: string) {
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86_400_000));
}

/** The user's own coupons for the item being bought, soonest expiry first. */
export const CouponPicker: React.FC<CouponPickerProps> = ({
  target,
  appliedCodes,
  disabled,
  onPick,
}) => {
  const { t, i18n } = useTranslation();
  const [coupons, setCoupons] = useState<MyCoupon[]>([]);

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    void getMyCoupons({
      kind: target.kind,
      itemPublicId: target.itemPublicId,
      billingPeriod: target.billingPeriod,
    }).then((res) => {
      if (!cancelled) setCoupons(res.success && res.data ? res.data.items : []);
    });
    return () => {
      cancelled = true;
    };
  }, [target]);

  const money = (amount: number) =>
    new Intl.NumberFormat(i18n.language === 'vi' ? 'vi-VN' : 'en-US', {
      style: 'currency',
      currency: 'VND',
      maximumFractionDigits: 0,
    }).format(amount);

  const available = coupons.filter((coupon) => coupon.applicable !== false);
  if (available.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-slate-400">
        <Ticket size={14} />
        {t('subscription.coupon.mine', 'Your coupons')}
      </p>
      <div className="flex flex-wrap gap-2">
        {available.map((coupon) => {
          const applied = appliedCodes.includes(coupon.code);
          const days = daysLeft(coupon.expiresAt);
          const discount =
            coupon.discountMinor !== null
              ? `-${money(coupon.discountMinor)}`
              : coupon.discount.type === 'PERCENT'
                ? `-${(coupon.discount.percentBps ?? 0) / 100}%`
                : `-${money(coupon.discount.fixedAmountMinor ?? 0)}`;
          return (
            <Chip
              key={coupon.code}
              clickable={!applied && !disabled}
              disabled={disabled}
              color={applied ? 'success' : 'default'}
              variant={applied ? 'filled' : 'outlined'}
              onClick={applied ? undefined : () => onPick(coupon.code)}
              label={`${coupon.code} · ${discount} · ${
                days <= 1
                  ? t('subscription.coupon.expiresToday', 'expires today')
                  : t('subscription.coupon.expiresIn', 'expires in {{days}} days', { days })
              }`}
            />
          );
        })}
      </div>
    </div>
  );
};
